import type { Address } from 'viem'
import { getRobinhoodPublicClient } from '@/lib/chain/publicClient'
import {
  getPresaleFactoryAddress,
  isEthAddress,
  isOnChainPresaleEnabled,
} from '@/lib/launchpad/contracts'
import { queueLaunchVerification } from '@/lib/verifyLaunch'
import { clampPresaleLpPct, creatorPctFromLp, PRESALE_CREATOR_MAX_PCT, PRESALE_LP_DEFAULT_PCT } from '@/lib/protocol'
import { presaleAbi, presaleFactoryAbi } from '@/lib/presale/abi'
import { estimatePresaleSupplyWei } from '@/lib/presale/math'
import { listSimPresales, upsertOnChainPresale } from '@/lib/presale/store'
import type { SimPresale } from '@/lib/presale/types'
import { isAfterLiveReset, LIVE_LAUNCH_CUTOFF_MS, shouldListOnChainItem } from '@/lib/liveReset'
import { isHiddenLaunch, isHiddenListingAddress } from '@/lib/hiddenListings'

const MAX_PRESALES = 40
const CREATED_LOG_CHUNK = 8_000n

const erc20Abi = [
  { type: 'function', name: 'name', stateMutability: 'view', inputs: [], outputs: [{ type: 'string' }] },
  { type: 'function', name: 'symbol', stateMutability: 'view', inputs: [], outputs: [{ type: 'string' }] },
] as const

async function readIndexedAddresses(
  client: ReturnType<typeof getRobinhoodPublicClient>,
  factory: Address,
): Promise<Address[]> {
  const length = await client.readContract({
    address: factory,
    abi: presaleFactoryAbi,
    functionName: 'allPresalesLength',
  })
  if (length <= 0n)
    return []

  const count = Number(length > BigInt(MAX_PRESALES) ? BigInt(MAX_PRESALES) : length)
  const start = length - BigInt(count)
  const indexes = Array.from({ length: count }, (_, i) => start + BigInt(i))
  const addresses = await Promise.all(indexes.map(index => client.readContract({
    address: factory,
    abi: presaleFactoryAbi,
    functionName: 'allPresales',
    args: [index],
  })))
  return [...addresses].reverse()
}

function existingPresale(address: string): SimPresale | undefined {
  const key = address.toLowerCase()
  return listSimPresales().find((item) => {
    return item.id.toLowerCase() === key || item.contractAddress?.toLowerCase() === key
  })
}

function isoFromSeconds(value: bigint | undefined, fallback: string): string {
  if (!value || value <= 0n)
    return fallback
  const ms = Number(value) * 1000
  if (!Number.isFinite(ms))
    return fallback
  return new Date(ms).toISOString()
}

async function createdAtLookback(
  client: ReturnType<typeof getRobinhoodPublicClient>,
): Promise<{ fromBlock: bigint, latest: bigint } | undefined> {
  try {
    const latest = await client.getBlockNumber()
    const elapsedSec = Math.max(0, Math.ceil((Date.now() - LIVE_LAUNCH_CUTOFF_MS) / 1000))
    const lookback = BigInt(Math.min(elapsedSec + 50_000, 1_000_000))
    const fromBlock = latest > lookback ? latest - lookback : 0n
    return { fromBlock, latest }
  }
  catch {
    return undefined
  }
}

async function readPresaleCreatedTimes(
  client: ReturnType<typeof getRobinhoodPublicClient>,
  factory: Address,
): Promise<Map<string, number>> {
  const times = new Map<string, number>()
  const range = await createdAtLookback(client)
  if (!range)
    return times

  let toBlock = range.latest
  while (toBlock >= range.fromBlock) {
    const chunkStart = toBlock >= CREATED_LOG_CHUNK ? toBlock - CREATED_LOG_CHUNK + 1n : 0n
    const fromBlock = chunkStart < range.fromBlock ? range.fromBlock : chunkStart
    const logs = await client.getContractEvents({
      address: factory,
      abi: presaleFactoryAbi,
      eventName: 'PresaleCreated',
      fromBlock,
      toBlock,
    }).catch(() => [])
    const blockNums = [...new Set(logs.map(log => log.blockNumber))]
    const timestamps = new Map<bigint, number>()
    await Promise.all(blockNums.map(async (blockNumber) => {
      const block = await client.getBlock({ blockNumber }).catch(() => undefined)
      if (block)
        timestamps.set(blockNumber, Number(block.timestamp) * 1000)
    }))
    for (const log of logs) {
      const presale = log.args?.presale
      if (!isEthAddress(presale))
        continue
      const createdAtMs = timestamps.get(log.blockNumber)
      if (typeof createdAtMs === 'number' && createdAtMs > 0)
        times.set(presale.toLowerCase(), createdAtMs)
    }
    if (fromBlock <= range.fromBlock)
      break
    toBlock = fromBlock - 1n
  }
  return times
}

async function readPresaleCreatedAtMs(
  client: ReturnType<typeof getRobinhoodPublicClient>,
  factory: Address,
  presale: Address,
): Promise<number | undefined> {
  const range = await createdAtLookback(client)
  if (!range)
    return undefined
  let toBlock = range.latest
  while (toBlock >= range.fromBlock) {
    const chunkStart = toBlock >= CREATED_LOG_CHUNK ? toBlock - CREATED_LOG_CHUNK + 1n : 0n
    const fromBlock = chunkStart < range.fromBlock ? range.fromBlock : chunkStart
    const logs = await client.getContractEvents({
      address: factory,
      abi: presaleFactoryAbi,
      eventName: 'PresaleCreated',
      args: { presale },
      fromBlock,
      toBlock,
    }).catch(() => [])
    const log = logs[0]
    if (log) {
      const block = await client.getBlock({ blockNumber: log.blockNumber }).catch(() => undefined)
      return block ? Number(block.timestamp) * 1000 : undefined
    }
    if (fromBlock <= range.fromBlock)
      break
    toBlock = fromBlock - 1n
  }
  return undefined
}

async function mapPresale(
  client: ReturnType<typeof getRobinhoodPublicClient>,
  address: Address,
  createdAtMs?: number,
): Promise<SimPresale | undefined> {
  const existing = existingPresale(address)
  const [
    creator,
    token,
    imageUrl,
    softCap,
    hardCap,
    minContribution,
    maxContribution,
    tokensPerEth,
    listingRate,
    creatorPctRaw,
    totalSupply,
    raised,
    startsAt,
    endsAt,
    participants,
    cancelled,
    finalized,
    success,
  ] = await Promise.all([
    client.readContract({ address, abi: presaleAbi, functionName: 'creator' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'token' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'imageUrl' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'softCap' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'hardCap' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'minContribution' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'maxContribution' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'presaleRate' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'listingRate' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'creatorPct' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'totalSupply' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'raised' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'startsAt' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'endsAt' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'participants' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'cancelled' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'finalized' }).catch(() => undefined),
    client.readContract({ address, abi: presaleAbi, functionName: 'success' }).catch(() => undefined),
  ])

  let tokenName = existing?.name
  let tokenTicker = existing?.ticker
  if (isEthAddress(token) && (!tokenName || !tokenTicker)) {
    const [name, symbol] = await Promise.all([
      client.readContract({ address: token as Address, abi: erc20Abi, functionName: 'name' }).catch(() => undefined),
      client.readContract({ address: token as Address, abi: erc20Abi, functionName: 'symbol' }).catch(() => undefined),
    ])
    tokenName = tokenName || name
    tokenTicker = tokenTicker || symbol
  }

  const resolvedName = (tokenName || '').trim()
  const resolvedTicker = (tokenTicker || '').trim().toUpperCase()
  const resolvedCreator = creator || existing?.creatorAddress
  const resolvedHardCap = hardCap ?? existing?.hardCapWei
  const resolvedSoftCap = softCap ?? existing?.softCapWei
  if (!resolvedName || !resolvedTicker || !resolvedCreator || !resolvedHardCap || !resolvedSoftCap)
    return undefined

  const nowIso = new Date().toISOString()
  const tokensPerEthWei = tokensPerEth ?? existing?.tokensPerEthWei ?? 0n
  const listingRateWei = listingRate ?? existing?.listingRateWei ?? tokensPerEthWei
  const creatorPct = Number(creatorPctRaw)
  const lpPct = Number.isFinite(creatorPct)
    ? clampPresaleLpPct(100 - creatorPct)
    : clampPresaleLpPct(existing?.lpPct ?? PRESALE_LP_DEFAULT_PCT)
  const teamPct = Number.isFinite(creatorPct)
    ? Math.max(0, Math.min(PRESALE_CREATOR_MAX_PCT, Math.round(creatorPct)))
    : (existing?.teamPct ?? creatorPctFromLp(lpPct))
  const totalSupplyWei = totalSupply
    ?? existing?.totalSupplyWei
    ?? estimatePresaleSupplyWei(tokensPerEthWei, resolvedHardCap)
  const resolvedCreatedAtMs = (typeof createdAtMs === 'number' && createdAtMs > 0)
    ? createdAtMs
    : 0
  if (!isAfterLiveReset(resolvedCreatedAtMs))
    return undefined
  const createdAt = new Date(resolvedCreatedAtMs).toISOString()
  const startsAtIso = isoFromSeconds(startsAt, existing?.startsAt ?? nowIso)
  const startsMs = new Date(startsAtIso).getTime()
  const startDelaySeconds = Number.isFinite(startsMs) && startsMs > resolvedCreatedAtMs
    ? Math.max(0, Math.round((startsMs - resolvedCreatedAtMs) / 1000))
    : (existing?.startDelaySeconds ?? 0)

  const resolvedRaisedWei = typeof raised === 'bigint' ? raised : (existing?.raisedWei ?? 0n)
  // Only bump the last-contribution timestamp when the raised amount actually
  // changed since the previous hydrate; otherwise the "Recently Bought" feed
  // must keep showing the real last contribution time, not creation time.
  const contributed = existing ? existing.raisedWei !== resolvedRaisedWei : resolvedRaisedWei > 0n
  const resolvedLastContributionAtMs = contributed ? Date.now() : (existing?.lastContributionAtMs ?? resolvedCreatedAtMs)

  return {
    id: existing?.id ?? address.toLowerCase(),
    name: resolvedName,
    ticker: resolvedTicker,
    category: existing?.category ?? 'DeFi',
    tagline: existing?.tagline || `${resolvedName} ETH raise on Robinhood Chain.`,
    description: existing?.description || `${resolvedName} is a live Penguin Pad presale settled on-chain.`,
    imageUrl: (typeof imageUrl === 'string' && imageUrl.trim()) ? imageUrl.trim() : existing?.imageUrl,
    website: existing?.website,
    twitter: existing?.twitter,
    youtube: existing?.youtube,
    telegram: existing?.telegram,
    discord: existing?.discord,
    tiktok: existing?.tiktok,
    facebook: existing?.facebook,
    instagram: existing?.instagram,
    creatorAddress: resolvedCreator,
    contractAddress: address,
    tokenAddress: isEthAddress(token) ? token : existing?.tokenAddress,
    shareSlug: existing?.shareSlug,
    createdAt,
    startsAt: startsAtIso,
    endsAt: isoFromSeconds(endsAt, existing?.endsAt ?? nowIso),
    lastContributionAtMs: resolvedLastContributionAtMs,
    lpPct,
    softCapWei: resolvedSoftCap,
    hardCapWei: resolvedHardCap,
    minContributionWei: minContribution ?? existing?.minContributionWei ?? 0n,
    maxContributionWei: maxContribution ?? existing?.maxContributionWei ?? resolvedHardCap,
    tokensPerEthWei,
    listingRateWei,
    teamPct,
    startDelaySeconds,
    totalSupplyWei,
    cancelled: Boolean(cancelled ?? existing?.cancelled),
    finalized: Boolean(finalized) || Boolean(existing?.finalized),
    success: Boolean(finalized)
      ? (typeof success === 'boolean' ? success : existing?.success)
      : (existing?.success === true
          ? true
          : (typeof success === 'boolean' ? success : existing?.success)),
    raisedWei: resolvedRaisedWei,
    participants: typeof raised === 'bigint' && raised === 0n
      ? 0
      : (typeof participants === 'bigint'
          ? Number(participants)
          : (existing?.participants ?? 0)),
    pledges: typeof raised === 'bigint' && raised === 0n ? {} : (existing?.pledges ?? {}),
  }
}

export async function hydrateOnChainPresales() {
  if (typeof window === 'undefined' || !isOnChainPresaleEnabled())
    return

  const factory = getPresaleFactoryAddress()
  if (!isEthAddress(factory))
    return

  const client = getRobinhoodPublicClient()
  let addresses: Address[] = []
  try {
    addresses = await readIndexedAddresses(client, factory)
  }
  catch {
    return
  }

  const createdTimes = await readPresaleCreatedTimes(client, factory).catch(() => new Map<string, number>())
  const seen = new Set<string>()
  const eligible: Address[] = []
  for (const address of addresses) {
    if (!isEthAddress(address))
      continue
    const key = address.toLowerCase()
    if (seen.has(key))
      continue
    seen.add(key)
    if (isHiddenListingAddress(address))
      continue
    const createdAtMs = createdTimes.get(key)
    if (!createdAtMs || !isAfterLiveReset(createdAtMs))
      continue
    eligible.push(address)
  }

  const HYDRATE_BATCH = 4
  for (let i = 0; i < eligible.length; i += HYDRATE_BATCH) {
    const batch = eligible.slice(i, i + HYDRATE_BATCH)
    await Promise.all(batch.map(async (address) => {
      const createdAtMs = createdTimes.get(address.toLowerCase())
      try {
        const mapped = await mapPresale(client, address, createdAtMs)
        if (mapped && !isHiddenLaunch(mapped) && shouldListOnChainItem(createdAtMs, false))
          upsertOnChainPresale(mapped)
      }
      catch {
        // Skip presales whose ABI surface does not match this client.
      }
    }))
  }
}

export async function hydrateOnChainPresale(address: Address): Promise<SimPresale | undefined> {
  if (typeof window === 'undefined' || !isOnChainPresaleEnabled() || !isEthAddress(address) || isHiddenListingAddress(address))
    return undefined
  const factory = getPresaleFactoryAddress()
  if (!isEthAddress(factory))
    return undefined
  const client = getRobinhoodPublicClient()
  try {
    const createdAtMs = await readPresaleCreatedAtMs(client, factory, address)
    if (!createdAtMs || !isAfterLiveReset(createdAtMs))
      return undefined
    const mapped = await mapPresale(client, address, createdAtMs)
    if (mapped && !isHiddenLaunch(mapped) && shouldListOnChainItem(createdAtMs, false)) {
      upsertOnChainPresale(mapped)
      queueLaunchVerification({
        kind: 'presale',
        address,
        token: mapped.tokenAddress,
      })
      return mapped
    }
    return undefined
  }
  catch {
    return undefined
  }
}
